import {
  Iterable,
  Set,
  Map,
  OrderedMap,
  List,
  Collection,
  fromJS as _fromJS,
} from 'immutable';
import {when, either, lens} from '@packages/utilities/fp/utils';
import {weakMemo} from '@packages/utilities/memo';
import {None, Some, Option} from '@packages/utilities/fp/option';
import {curry, isUndefined} from 'lodash';

const NOT_SET_VALUE: Record<string, any> = {};

export const fromJS = _fromJS;

export const get = curry(function get(path: any, coll: any) {
  if (!coll) {
    return;
  }
  return Iterable.isIterable(path) || Array.isArray(path)
    ? coll.getIn(path)
    : coll.get(path);
});

export const getElse = curry(function getElse(
  path: any,
  notFound: any,
  coll: any
) {
  const value = get(path, coll);
  return isUndefined(value) ? notFound : value;
});

export const nth =
  (index: number) =>
  <T>(list: List<T>): Option<T> =>
    index < 0 || index >= list.size ? None : Some(list.get(index) as T);

export const first = nth(0);

export const last = <T>(list: List<T>): Option<any> =>
  list.size ? Some(list.last()) : None;

export const getIn = curry(function getIn(keys: any, coll: any) {
  if (!coll) {
    // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
    return Iterable();
  }
  return isPrimitive(keys) ? coll.get(keys) : coll.getIn(keys);
});

export const set = curry(function set(keys: any, v: any, coll: any) {
  const path = keyPath(keys);
  return coll.setIn(path, v);
});

export const assoc =
  <K>(key: K) =>
  <V>(val: V) =>
  (coll: Map<K, V>): Map<K, V> =>
    coll.set(key, val);

export const setIn = curry(function setIn(keys: any, v: any, coll: any) {
  return coll.setIn(keys, v);
});

export const update = curry(function update(fn: any, keys: any, coll: any) {
  const path = keyPath(keys);
  return coll.updateIn(path, fn);
});

export const remove = curry(function remove(key: any, coll: any) {
  return coll.remove(key);
});

export const updateIn = curry(function updateIn(fn: any, keys: any, coll: any) {
  if (!coll) {
    // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
    return Iterable();
  }
  return isPrimitive(keys) ? coll.update(keys, fn) : coll.updateIn(keys, fn);
});

export const removeIn = curry(function removeIn(keys: any, coll: any) {
  return coll.deleteIn(keys);
});

export const mergeIn = curry(function mergeIn(keys: any, v: any, coll: any) {
  return coll.mergeIn(keys, v);
});

export const merge = curry(function merge(v: any, coll: any) {
  return coll.merge(v);
});

export const mergeDeep = curry(function mergeDeep(v: any, coll: any) {
  return coll.mergeDeep(v);
});

export const append = curry(function append(v: any, coll: any) {
  return coll.concat([v]);
});

export const prepend = curry(function prepend(v: any, coll: any) {
  return coll.unshift(v);
});

export const concat = curry(function concat(coll: any, coll2: any) {
  return coll.concat(coll2);
});

export const push = curry(function push(v: any, coll: any) {
  return coll.push(v);
});

export const add = curry(function add(v: any, coll: any) {
  return coll.add(v);
});

export const insert = curry(function insert(k: any, v: any, coll: any) {
  return coll.splice(k, 0, v);
});

export const reduce = curry(function reduce(fn: any, initial: any, coll: any) {
  return coll.reduce(fn, initial);
});

export const slice = curry(function slice(from: any, to: any, coll: any) {
  return coll.slice(from, to);
});

export const pick = curry((keys: any, coll: any) =>
  keys.reduce(
    (m: any, k: any) => (coll.has(k) ? m.set(k, coll.get(k)) : m),
    coll.clear()
  )
);

export const find =
  <K, T>(
    predicate: (value: T, key: K) => boolean
  ): ((arg1: Collection<K, T>) => Option<T>) =>
  (coll: Collection<K, T>) => {
    // @ts-expect-error - TS2345 - Argument of type '(value: T, key: K) => boolean' is not assignable to parameter of type '(value?: T | undefined, key?: K | undefined, iter?: Iterable<K, T> | undefined) => boolean'.
    const found = coll.find(predicate, null, NOT_SET_VALUE);
    return found === NOT_SET_VALUE ? None : Some(found as T);
  };

export function head<T>(coll: List<T>): T | null | undefined {
  return coll && coll.first();
}

export function init<T>(coll: List<T>): List<T> {
  return coll.butLast();
}

export function contains<T>(v: T, coll: List<T>): boolean {
  return coll ? coll.includes(v) : false;
}

export const reverse = <T>(coll: List<T>): List<T> => coll.reverse();
export const clear = <T>(coll: List<T>): List<T> => coll.clear();

export const propIs = curry(
  (key: any, value: any, imm: any) => get(key, imm) === value
);

/**
 * Returns a duplicate-free list.
 * @param  {Function} fn   Responsible for creating value used in comparisons.
 * @param  {Array}    list
 * @return {Array}
 */
export const uniqueBy = curry((fn: any, list: any) =>
  OrderedMap(
    list.map((item: any, index: any) => [fn(item, index, list), item])
  ).toList()
);

const isPrimitive = either(isString, isNumber);
const keyPath = when(isPrimitive)(Array.of);

function isString(x: unknown): boolean {
  return typeof x === 'string';
}

function isNumber(x: unknown): boolean {
  return typeof x === 'number';
}

/**
 * Returns a Set of keys of an Iterable that have the highest value returned by a getter function
 * @param fn getter function for the value to compare
 * @param coll an Iterable that has the values to compare
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export const maxKeys = <F extends Function, K, T>(
  fn: F,
  coll: Iterable<K, T>
) =>
  coll.reduce(
    // @ts-expect-error - TS2345 - Argument of type '([keys, current]: [any, any], v: T | undefined, k: number | undefined) => any[]' is not assignable to parameter of type '(reduction?: [any, any] | undefined, value?: T | undefined, key?: number | undefined, iter?: Iterable<number, T> | undefined) => [any, any]'.
    ([keys, current]: [any, any], v, k) => {
      const res = fn(v);
      if (res === current) {
        return [keys.add(k), current];
      }
      if (res > current || current == null) {
        return [Set.of(k), res];
      }

      return [keys, current];
    },
    [Set(), null]
  )[0];

export const makeLensFactoryWithDefault =
  <D>(fallback: D) =>
  <K extends keyof any>(key: K) =>
    // @ts-expect-error - TS18046 - 'coll' is of type 'unknown'.
    lens((coll) => coll.get(key, fallback))(
      (val) => (coll: any) =>
        val === fallback ? coll.delete(key) : coll.set(key, val)
    );

// @ts-expect-error - TS7006 - Parameter 'coll' implicitly has an 'any' type.
export const toJS = weakMemo((coll) => coll.toJS());

const placeholder = {'@webflow/notSetPlaceholder': true} as const;

export const lookup =
  <k>(key: k): (<v>(arg1: Map<k, v>) => Option<v>) =>
  <v>(map: Map<k, v>) => {
    // @ts-expect-error - TS2345 - Argument of type '{ readonly '@webflow/notSetPlaceholder': true; }' is not assignable to parameter of type 'v'.
    const result = map.get(key, placeholder);
    return result === placeholder ? None : Some(result);
  };

export const memoFromJS = weakMemo((jsValue: any) => fromJS(jsValue));
